<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { useRouter } from 'vue-router'

import AdvertiserReportListItem from './AdvertiserReportListItem.vue'
import CreateAdvertiserReportModal from './CreateAdvertiserReportModal.vue'

import { useGetAdvertiserReports } from '@/api'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  storeId: number
  defaultLanguage?: string
}>()

const router = useRouter()

const storeIdRef = toRef(props, 'storeId')
const { data: reports, isLoading } = useGetAdvertiserReports(storeIdRef)

const isModalOpen = ref(false)

const isInitialLoading = computed(() => isLoading.value && reports.value.length === 0)
const isEmpty = computed(() => !isLoading.value && reports.value.length === 0)

const openReport = (uuid: string) => {
  void router.push({ name: 'advertiserReportEditor', params: { id: props.storeId, uuid } })
}

const onCreated = (uuid: string) => {
  isModalOpen.value = false
  openReport(uuid)
}
</script>

<template>
  <BoxComponent :p="24">
    <FlexComponent justify="flex-start" align="center" :gap="16">
      <ButtonComponent variant="primary" @click="isModalOpen = true">Create new report</ButtonComponent>
    </FlexComponent>

    <BoxComponent :mt="24">
      <LoadingComponent v-if="isInitialLoading" :size="48" />
      <TypographyComponent v-else-if="isEmpty" variant="body-large" color="var(--secondary-48)">
        No reports yet. Create one to get started.
      </TypographyComponent>
      <FlexComponent v-else direction="column" :gap="16">
        <AdvertiserReportListItem v-for="report in reports" :key="report.uuid" :report="report" @open="openReport(report.uuid)" />
      </FlexComponent>
    </BoxComponent>

    <CreateAdvertiserReportModal
      v-if="isModalOpen"
      v-model="isModalOpen"
      :store-id="storeId"
      :default-language="defaultLanguage"
      @created="onCreated"
    />
  </BoxComponent>
</template>
